import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { X, FileText, RefreshCw, Plus, Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAdminApi } from "../../providers/api-context";
import { Button } from "@carry/ui";
import { formatYen } from "@carry/shared";
import { toast } from "sonner";
import { type OrderDto, type ParsedOrderPreviewDto } from "@carry/contract";
import { OrderPdfPanel } from "./order-pdf-panel";
import { parsedPreviewToDraft, type ParsedOrderPreview } from "./parsed-preview";
import {
  buildCreateOrderPayload,
  buildImportUpdateOrderPayload,
  type ResolvedCoords,
} from "./order-payload";
import { getOrderBlockingReasons } from "./form-ux";
import type { DraftOrder } from "./types";
import { createOrderMasterCache, ensureUnselectedOrderMasters } from "./auto-master-registration";
import { geocodeJpAddress } from "./geocoder";

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8080";
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const IMPORT_UPLOAD_BATCH_SIZE = 1;

export interface ImportResponse {
  previews: ParsedOrderPreviewDto[];
  errors: { file_name: string; error: string }[];
}

export interface ImportUploadProgress {
  completed: number;
  total: number;
  currentFileName: string | null;
}

interface PreviewEntry {
  id: string;
  preview: ParsedOrderPreview;
  sourceFileName: string;
  status: "pending" | "confirmed";
  createdOrderPublicId: string | null;
  existingDuplicatePublicId: string | null;
  error: string | null;
}

export interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * ParsedOrderPreviewDto (contract) → ParsedOrderPreview (parsed-preview.ts) の変換。
 * pickup_lat/pickup_lng は取引先マスタ補完時のみ Dto に入る (Issue #613)。
 * delivery_lat/delivery_lng は Dto にないため null で補完する。
 */
function dtoToPreview(dto: ParsedOrderPreviewDto): ParsedOrderPreview {
  const extra = dto as ParsedOrderPreviewDto & {
    delivery_lat?: number | null;
    delivery_lng?: number | null;
    pickup_lat?: number | null;
    pickup_lng?: number | null;
  };
  return {
    ...dto,
    delivery_lat: extra.delivery_lat ?? null,
    delivery_lng: extra.delivery_lng ?? null,
    pickup_lat: extra.pickup_lat ?? null,
    pickup_lng: extra.pickup_lng ?? null,
  };
}

function previewCoords(preview: ParsedOrderPreview): ResolvedCoords {
  return {
    pickup:
      preview.pickup_lat != null && preview.pickup_lng != null
        ? { lat: preview.pickup_lat, lng: preview.pickup_lng }
        : null,
    delivery:
      preview.delivery_lat != null && preview.delivery_lng != null
        ? { lat: preview.delivery_lat, lng: preview.delivery_lng }
        : null,
  };
}

export async function uploadOrderFilesInBatches(
  list: File[],
  fetchImpl: typeof fetch = fetch,
  onProgress?: (progress: ImportUploadProgress) => void,
): Promise<ImportResponse> {
  const result: ImportResponse = { previews: [], errors: [] };
  for (let start = 0; start < list.length; start += IMPORT_UPLOAD_BATCH_SIZE) {
    const batch = list.slice(start, start + IMPORT_UPLOAD_BATCH_SIZE);
    const form = new FormData();
    for (const f of batch) form.append("files", f);
    onProgress?.({
      completed: start,
      total: list.length,
      currentFileName: batch.map((f) => f.name).join(", "),
    });

    let res: Response;
    try {
      res = await fetchImpl(`${API_BASE_URL}/api/v1/orders/import`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
    } catch {
      throw new Error(
        "注文書ファイルの送信に失敗しました。通信状態を確認して、少ない件数で再実行してください",
      );
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      const fallback =
        res.status === 503
          ? "注文書の解析に時間がかかりサーバーが処理を中断しました。少ない件数で再実行してください"
          : `HTTP ${res.status}`;
      throw new Error(body.message ?? body.error ?? fallback);
    }

    const body = (await res.json()) as ImportResponse;
    result.previews.push(...body.previews);
    result.errors.push(...(body.errors ?? []));
    onProgress?.({
      completed: Math.min(start + batch.length, list.length),
      total: list.length,
      currentFileName: null,
    });
  }
  return result;
}

/**
 * 注文書一括取込モーダル.
 * ファイルを選択・ドロップすると即解析し、要確認 (注文番号重複 / 必須項目欠落・解析失敗 /
 * 信頼度 < 80%) に該当しないプレビューはその場で下書き受注として自動作成する。
 * 要確認に該当したものだけカードに残し、ユーザーが取込可否を判断する。
 */
export function BulkImportModal({ open, onClose }: BulkImportModalProps) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [entries, setEntries] = useState<PreviewEntry[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<ImportUploadProgress | null>(null);
  const [fileErrors, setFileErrors] = useState<{ file_name: string; error: string }[]>([]);
  const [classifying, setClassifying] = useState(false);
  const [recentlyRemoved, setRecentlyRemoved] = useState<{
    entry: PreviewEntry;
    index: number;
  } | null>(null);
  const masterCacheRef = useRef(createOrderMasterCache());

  // 解析済みプレビューを分類し、要確認に該当しないものは下書き受注として自動作成する。
  // 要確認 = 必須項目欠落 (getConfirmBlockers) / 信頼度 < 0.8 / 注文番号重複。
  // マスタ未マッチは要確認に含めない (自動作成する)。
  const autoCreateClean = async (newEntries: PreviewEntry[]) => {
    setClassifying(true);
    let createdCount = 0;
    let createdDeliverySpot = false;
    let createdPickupSpot = false;
    for (const entry of newEntries) {
      const preview = entry.preview;
      const blockers = getConfirmBlockers(preview);
      const lowConfidence = (preview.confidence ?? 1) < 0.8;
      const orderNumber = preview.order_number?.trim();
      let duplicateId: string | null = null;
      if (orderNumber) {
        try {
          const dup = await api.order.list({ limit: 1, order_number: orderNumber });
          duplicateId = dup.data[0]?.id ?? null;
        } catch {
          // 照会失敗時はカード側の重複検出に委ねる (安全側で自動作成しない)
          duplicateId = null;
        }
      }
      const needsReview = blockers.length > 0 || lowConfidence || !!duplicateId;
      if (needsReview) {
        if (duplicateId) {
          setEntries((prev) =>
            prev.map((e) =>
              e.id === entry.id ? { ...e, existingDuplicatePublicId: duplicateId } : e,
            ),
          );
        }
        continue;
      }
      try {
        const draft = parsedPreviewToDraft(preview);
        const ensured = await ensureUnselectedOrderMasters({
          api,
          draft,
          coords: previewCoords(preview),
          geocodeAddress: geocodeJpAddress,
          cache: masterCacheRef.current,
        });
        createdDeliverySpot ||= ensured.createdDeliverySpot;
        createdPickupSpot ||= ensured.createdPickupSpot;
        const payload = buildCreateOrderPayload(ensured.draft, ensured.coords);
        const created = await api.order.create(payload);
        createdCount += 1;
        setEntries((prev) =>
          prev.map((e) =>
            e.id === entry.id
              ? { ...e, status: "confirmed", createdOrderPublicId: created.data.id, error: null }
              : e,
          ),
        );
      } catch (err) {
        setEntries((prev) =>
          prev.map((e) =>
            e.id === entry.id
              ? { ...e, error: err instanceof Error ? err.message : String(err) }
              : e,
          ),
        );
      }
    }
    if (createdDeliverySpot) {
      await queryClient.invalidateQueries({ queryKey: ["delivery-spots"] });
    }
    if (createdPickupSpot) {
      await queryClient.invalidateQueries({ queryKey: ["pickup-spots"] });
    }
    if (createdCount > 0) {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success(`${createdCount} 件を下書きとして作成しました`);
    }
    setClassifying(false);
  };

  const uploadMutation = useMutation({
    mutationFn: async (list: File[]): Promise<ImportResponse> => {
      return uploadOrderFilesInBatches(list, fetch, setUploadProgress);
    },
    onSuccess: (res) => {
      const newEntries: PreviewEntry[] = res.previews.map((p, i) => ({
        id: `${Date.now()}_${i}`,
        preview: dtoToPreview(p),
        sourceFileName: p.source_file_name,
        status: "pending",
        createdOrderPublicId: null,
        existingDuplicatePublicId: null,
        error: null,
      }));
      setEntries((prev) => [...newEntries, ...prev]);
      setFiles([]);
      setUploadProgress(null);
      setFileErrors(res.errors ?? []);
      setUploadError(null);
      void autoCreateClean(newEntries);
    },
    onError: (err) => {
      setUploadProgress(null);
      setUploadError(err instanceof Error ? err.message : String(err));
    },
  });

  const addFiles = (incoming: File[]) => {
    if (uploadMutation.isPending) return;
    setUploadError(null);
    const filtered = incoming.filter((f) => f.type.includes("pdf") || /\.pdf$/i.test(f.name));
    const skipped = incoming.length - filtered.length;
    if (skipped > 0) setUploadError(`${skipped} 件は PDF ではないため除外しました`);
    const oversize = filtered.filter((f) => f.size > MAX_FILE_BYTES);
    if (oversize.length > 0) {
      setUploadError(`${oversize.map((f) => f.name).join(", ")} は 10MB を超えています`);
      return;
    }
    // state 更新前の現在値を使って重複排除し、即時 mutate に渡す
    const deduped: File[] = [];
    for (const f of filtered) {
      if (
        !files.some((n) => n.name === f.name && n.size === f.size) &&
        !deduped.some((n) => n.name === f.name && n.size === f.size)
      ) {
        deduped.push(f);
      }
    }
    const next = [...files, ...deduped];
    setFiles(next);
    if (next.length > 0) {
      uploadMutation.mutate(next);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const list = Array.from(e.dataTransfer?.files ?? []);
    if (list.length > 0) addFiles(list);
  };

  const createdCount = entries.filter((entry) => entry.status === "confirmed").length;
  const reviewCount = entries.filter((entry) => entry.status === "pending").length;
  const failedCount = entries.filter((entry) => entry.error).length;

  const updateEntry = (updated: PreviewEntry) => {
    setEntries((prev) => prev.map((entry) => (entry.id === updated.id ? updated : entry)));
  };
  const removeEntry = (entryId: string) => {
    const index = entries.findIndex((entry) => entry.id === entryId);
    if (index < 0) return;
    setRecentlyRemoved({ entry: entries[index]!, index });
    setEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  };
  const restoreRemovedEntry = () => {
    if (!recentlyRemoved) return;
    setEntries((prev) => {
      const next = [...prev];
      next.splice(Math.min(recentlyRemoved.index, next.length), 0, recentlyRemoved.entry);
      return next;
    });
    setRecentlyRemoved(null);
  };

  const handleClose = () => {
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    setEntries([]);
    setFiles([]);
    setFileErrors([]);
    setUploadError(null);
    setUploadProgress(null);
    setRecentlyRemoved(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-import-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        background: "oklch(0 0 0 / 0.28)",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          width: "min(1200px, 100%)",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          background: "var(--bg)",
          border: "0.5px solid var(--rule)",
          borderRadius: 4,
          boxShadow: "0 18px 40px oklch(0 0 0 / 0.18)",
          overflow: "hidden",
        }}
      >
        <header
          style={{
            padding: "16px 20px",
            borderBottom: "0.5px solid var(--rule-soft)",
            background: "var(--bg-panel)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div id="bulk-import-title" style={{ fontWeight: 600, fontSize: 14 }}>
            注文書一括取込
          </div>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            aria-label="閉じる"
            onClick={handleClose}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--ink-3)",
              padding: 4,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              borderRadius: 2,
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </header>

        <div
          style={{
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            background: "var(--bg)",
          }}
        >
          <div
            style={{
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
              ファイルを選択・ドロップすると、要確認を除き自動で下書き作成します
            </div>
            <DropZone onDrop={onDrop} onFilesPicked={addFiles} />

            {uploadError && (
              <div
                role="alert"
                style={{
                  padding: "10px 14px",
                  background: "oklch(0.96 0.05 30)",
                  border: "0.5px solid var(--danger)",
                  color: "var(--danger)",
                  fontSize: 12,
                }}
              >
                {uploadError}
              </div>
            )}

            {uploadMutation.isPending && (
              <div
                style={{
                  border: "0.5px solid var(--rule)",
                  borderRadius: 3,
                  background: "var(--bg-raised)",
                  padding: "14px 18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>注文書を1ファイルずつ解析中</span>
                  <span style={{ color: "var(--ink-3)", marginLeft: 8 }}>{files.length} 件</span>
                </div>
                {uploadProgress && <UploadProgressIndicator progress={uploadProgress} />}
              </div>
            )}

            {fileErrors.length > 0 && (
              <div
                style={{
                  padding: "10px 14px",
                  background: "oklch(0.96 0.05 60)",
                  border: "0.5px solid var(--warn)",
                  color: "var(--warn)",
                  fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  一部ファイルで解析に失敗しました
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {fileErrors.map((e, i) => (
                    <li key={i}>
                      <code>{e.file_name}</code>: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {entries.length > 0 && (
              <SummaryBanner
                created={createdCount}
                review={reviewCount}
                failed={failedCount}
                classifying={classifying}
              />
            )}

            {recentlyRemoved && (
              <div
                role="status"
                style={{
                  padding: "10px 14px",
                  background: "var(--bg-panel)",
                  border: "0.5px solid var(--rule)",
                  color: "var(--ink-2)",
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span style={{ flex: 1 }}>
                  {recentlyRemoved.entry.sourceFileName} を破棄しました
                </span>
                <Button type="button" variant="outline" size="sm" onClick={restoreRemovedEntry}>
                  元に戻す
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRecentlyRemoved(null)}
                >
                  閉じる
                </Button>
              </div>
            )}
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: 14, padding: "0 20px 20px" }}
          >
            {entries.map((entry) => (
              <PreviewCard
                key={entry.id}
                entry={entry}
                masterCache={masterCacheRef.current}
                onUpdate={updateEntry}
                onRemove={() => removeEntry(entry.id)}
              />
            ))}
          </div>
        </div>

        <footer
          style={{
            padding: "12px 20px",
            borderTop: "0.5px solid var(--rule-soft)",
            background: "var(--bg-panel)",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button type="button" variant="default" size="sm" onClick={handleClose}>
            閉じる
          </Button>
        </footer>
      </div>
    </div>
  );
}

function UploadProgressIndicator({ progress }: { progress: ImportUploadProgress }) {
  const percent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  const activeIndex = Math.min(
    progress.completed + (progress.currentFileName ? 1 : 0),
    progress.total,
  );
  return (
    <div
      role="status"
      aria-label="注文書取込の進捗"
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11 }}>
        <span style={{ fontWeight: 500 }}>
          {progress.currentFileName
            ? `${activeIndex} / ${progress.total} 件目を解析中`
            : `${progress.completed} / ${progress.total} 件完了`}
        </span>
        {progress.currentFileName && (
          <span
            style={{
              minWidth: 0,
              color: "var(--ink-3)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {progress.currentFileName}
          </span>
        )}
      </div>
      <div
        aria-hidden="true"
        style={{
          width: "100%",
          height: 4,
          borderRadius: 999,
          background: "var(--rule-soft)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            background: "var(--accent)",
            transition: "width 160ms ease",
          }}
        />
      </div>
    </div>
  );
}

function SummaryBanner({
  created,
  review,
  failed,
  classifying,
}: {
  created: number;
  review: number;
  failed: number;
  classifying: boolean;
}) {
  return (
    <div
      style={{
        border: "0.5px solid var(--rule)",
        borderRadius: 3,
        background: "var(--bg-panel)",
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
        fontSize: 12,
      }}
    >
      <span style={{ color: "var(--ok)", fontWeight: 500 }}>✓ 取込済み {created}</span>
      <span style={{ color: review > 0 ? "var(--warn)" : "var(--ink-3)", fontWeight: 500 }}>
        ⚠ 要確認 {review}
      </span>
      {failed > 0 && <span style={{ color: "var(--danger)", fontWeight: 500 }}>失敗 {failed}</span>}
      <span style={{ flex: 1 }} />
      {classifying ? (
        <span style={{ color: "var(--ink-3)" }}>下書き作成中…</span>
      ) : (
        review > 0 && (
          <span style={{ color: "var(--ink-3)" }}>
            要確認の {review} 件はカードで取込可否を判断してください
          </span>
        )
      )}
    </div>
  );
}

function DropZone({
  onDrop,
  onFilesPicked,
}: {
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
  onFilesPicked: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    if (list.length > 0) onFilesPicked(list);
    e.target.value = "";
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        setDragging(false);
        onDrop(e);
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      style={{
        padding: "44px 18px",
        textAlign: "center",
        border: dragging ? "1px dashed var(--accent)" : "1px dashed var(--rule)",
        background: dragging ? "oklch(0.97 0.02 250)" : "var(--bg-raised)",
        borderRadius: 4,
        cursor: "pointer",
      }}
    >
      <div style={{ marginBottom: 6, display: "flex", justifyContent: "center" }}>
        <FileText size={36} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
        ここに PDF をドロップ or クリックして選択
      </div>
      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>各ファイル 10MB まで</div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,.pdf"
        onChange={onChange}
        style={{ display: "none" }}
      />
    </div>
  );
}

/**
 * autoCreateClean ゲート用ブロッカー判定 (ParsedOrderPreview 上で動作)。
 * カードのボタン活性判定は getDraftImportBlockers (DraftOrder 上) を使う。
 */
function getConfirmBlockers(preview: ParsedOrderPreview): string[] {
  const blockers: string[] = [];
  if (!preview.order_number?.trim()) blockers.push("注文番号");
  if (!preview.delivery_date) blockers.push("配送日");
  if (!preview.delivery_address.trim()) blockers.push("配送先住所");
  if (preview.items.length === 0) blockers.push("商品明細");
  preview.items.forEach((item, index) => {
    const rowNo = index + 1;
    if (!item.product_name.trim()) blockers.push(`明細${rowNo}の品目名`);
    if (item.quantity < 1) blockers.push(`明細${rowNo}の数量`);
  });
  return blockers;
}

function getDraftImportBlockers(value: DraftOrder): string[] {
  const blockers: string[] = [];
  if (!value.order_number.trim()) blockers.push("注文番号");
  if (!value.delivery_address.trim()) blockers.push("配送先住所");
  if (value.delivery_date.length !== 10) blockers.push("配送日");
  if (value.items.length === 0) {
    blockers.push("明細");
  } else {
    const invalidItemIndex = value.items.findIndex(
      (item) => !item.product_name.trim() || item.quantity < 1,
    );
    if (invalidItemIndex >= 0) blockers.push(`明細${invalidItemIndex + 1}行目`);
  }
  return blockers;
}

function ConfirmBlockers({ blockers }: { blockers: string[] }) {
  return (
    <div
      role="alert"
      style={{
        padding: "8px 18px",
        borderBottom: "0.5px solid var(--rule-soft)",
        background: "oklch(0.97 0.03 60)",
        color: "var(--warn)",
        fontSize: 11,
      }}
    >
      確定に必要な項目: {blockers.join("、")}
    </div>
  );
}

function ConfidenceBadge({ level, pct }: { level: "mid" | "low"; pct: number }) {
  const map = {
    mid: { bg: "var(--st-assigned-bg)", ink: "var(--st-assigned-ink)", label: "要確認" },
    low: { bg: "var(--st-cancel-bg)", ink: "var(--st-cancel-ink)", label: "手動補完" },
  }[level];
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
      <div className="num" style={{ fontSize: 18, fontWeight: 500 }}>
        {pct}%
      </div>
      <span
        style={{
          fontSize: 10,
          padding: "2px 8px",
          background: map.bg,
          color: map.ink,
          borderRadius: 2,
          fontWeight: 500,
        }}
      >
        {map.label}
      </span>
    </div>
  );
}

interface ImportComparisonRow {
  label: string;
  current: string;
  imported: string;
  changed: boolean;
}

// 未入力の値は「—」等のプレースホルダを使わず空文字で表示する。
function valueText(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

// YYYY-MM-DD → YYYY/M/D (ゼロ埋めなし)。日付形式でない値はそのまま表示する。
function dateText(value: string | null | undefined): string {
  const text = valueText(value);
  const m = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return text;
  return `${m[1]}/${Number(m[2])}/${Number(m[3])}`;
}

function rangeText(start: string | null | undefined, end: string | null | undefined): string {
  const from = valueText(start);
  const to = valueText(end);
  if (!from && !to) return "";
  return `${from} - ${to}`;
}

function timeWindowsText(windows: Array<{ from: string; to: string }>): string {
  if (windows.length === 0) return "";
  return windows.map((window) => rangeText(window.from, window.to)).join("\n");
}

function minutesText(value: number | null | undefined): string {
  return value == null ? "" : `${value} 分`;
}

function itemsText(
  items: Array<{ product_name: string; quantity: number; unit_price: number | null }>,
): string {
  if (items.length === 0) return "";
  return items
    .map((item, index) => {
      const price = item.unit_price == null ? "" : ` / ${formatYen(item.unit_price)}`;
      return `${index + 1}. ${valueText(item.product_name)} x ${item.quantity}${price}`;
    })
    .join("\n");
}

function comparisonRow(label: string, current: string, imported: string): ImportComparisonRow {
  return {
    label,
    current,
    imported,
    changed: current !== imported,
  };
}

export function buildComparisonRows(
  existing: OrderDto | null,
  draft: DraftOrder,
): ImportComparisonRow[] {
  return [
    comparisonRow("注文番号", valueText(existing?.order_number), valueText(draft.order_number)),
    comparisonRow(
      "注文元ID・決済番号",
      valueText(existing?.payment_number),
      valueText(draft.payment_number),
    ),
    // 支払区分は自由テキストのため丸めが起きず、他のテキスト項目と同じ単純比較でよい.
    comparisonRow("支払区分", valueText(existing?.payment_type), valueText(draft.payment_type)),
    comparisonRow("配送日", dateText(existing?.delivery_date), dateText(draft.delivery_date)),
    comparisonRow(
      "配送時刻",
      rangeText(existing?.delivery_time_start, existing?.delivery_time_end),
      rangeText(draft.delivery_time_start, draft.delivery_time_end),
    ),
    comparisonRow(
      "納品作業時間",
      minutesText(existing?.delivery_duration_minutes),
      minutesText(draft.delivery_duration_minutes),
    ),
    comparisonRow(
      "配送先住所",
      valueText(existing?.delivery_address),
      valueText(draft.delivery_address),
    ),
    comparisonRow(
      "ビル・施設名",
      valueText(existing?.delivery_building),
      valueText(draft.delivery_building),
    ),
    comparisonRow(
      "引渡し場所",
      valueText(existing?.handover_location),
      valueText(draft.handover_location),
    ),
    comparisonRow(
      "到着後の対応",
      valueText(existing?.arrival_instructions),
      valueText(draft.arrival_instructions),
    ),
    comparisonRow(
      "会社名",
      valueText(existing?.delivery_company),
      valueText(draft.delivery_company),
    ),
    comparisonRow(
      "支店・営業所",
      valueText(existing?.delivery_branch),
      valueText(draft.delivery_branch),
    ),
    comparisonRow(
      "担当者氏名",
      valueText(existing?.delivery_person_name),
      valueText(draft.delivery_person_name),
    ),
    comparisonRow(
      "担当者名（カナ）",
      valueText(existing?.delivery_person_name_kana),
      valueText(draft.delivery_person_name_kana),
    ),
    comparisonRow(
      "連絡先電話番号",
      valueText(existing?.delivery_phone),
      valueText(draft.delivery_phone),
    ),
    comparisonRow(
      "メールアドレス",
      valueText(existing?.delivery_email),
      valueText(draft.delivery_email),
    ),
    comparisonRow("集荷先名", valueText(existing?.store_name), valueText(draft.store_name)),
    comparisonRow("集荷先住所", valueText(existing?.store_address), valueText(draft.store_address)),
    comparisonRow("集荷先電話番号", valueText(existing?.store_phone), valueText(draft.store_phone)),
    comparisonRow(
      "集荷時刻",
      rangeText(existing?.pickup_time_start, existing?.pickup_time_end),
      rangeText(draft.pickup_time_start, draft.pickup_time_end),
    ),
    comparisonRow(
      "集荷不可時間",
      timeWindowsText(existing?.pickup_unavailable_times ?? []),
      timeWindowsText(draft.pickup_unavailable_times),
    ),
    comparisonRow(
      "集荷作業時間",
      minutesText(existing?.pickup_duration_minutes),
      minutesText(draft.pickup_duration_minutes),
    ),
    comparisonRow(
      "納品書荷姿",
      valueText(existing?.delivery_packaging),
      valueText(draft.delivery_packaging),
    ),
    comparisonRow(
      "店舗様への伝言",
      valueText(existing?.store_message),
      valueText(draft.store_message),
    ),
    comparisonRow(
      "納品先からのご要望",
      valueText(existing?.delivery_notes),
      valueText(draft.delivery_notes),
    ),
    comparisonRow("備考", valueText(existing?.remarks), valueText(draft.remarks)),
    comparisonRow("明細", itemsText(existing?.items ?? []), itemsText(draft.items)),
  ];
}

function ImportComparisonPanel({
  existing,
  draft,
}: {
  existing: OrderDto | null;
  draft: DraftOrder;
}) {
  const rows = buildComparisonRows(existing, draft);
  const changedCount = rows.filter((row) => row.changed).length;
  return (
    <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
        <span style={{ fontWeight: 600 }}>{existing ? "既存受注との差分" : "読み取り結果"}</span>
        <span style={{ color: "var(--ink-3)" }}>
          {existing ? `${changedCount} 項目に差分` : "現在の既存受注はありません"}
        </span>
      </div>
      <div
        style={{
          border: "0.5px solid var(--rule)",
          borderRadius: 3,
          overflow: "hidden",
          fontSize: 11,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "150px minmax(0, 1fr) minmax(0, 1fr)",
            background: "var(--bg-panel)",
            borderBottom: "0.5px solid var(--rule)",
            fontWeight: 600,
          }}
        >
          <div style={{ padding: "8px 10px" }}>項目</div>
          <div style={{ padding: "8px 10px", borderLeft: "0.5px solid var(--rule-soft)" }}>
            現在の値
          </div>
          <div style={{ padding: "8px 10px", borderLeft: "0.5px solid var(--rule-soft)" }}>
            読み取り値
          </div>
        </div>
        {rows.map((row) => (
          <div
            key={row.label}
            style={{
              display: "grid",
              gridTemplateColumns: "150px minmax(0, 1fr) minmax(0, 1fr)",
              borderBottom: "0.5px solid var(--rule-soft)",
              background: row.changed ? "oklch(0.98 0.03 80)" : "var(--bg-raised)",
            }}
          >
            <div style={{ padding: "8px 10px", color: "var(--ink-3)" }}>{row.label}</div>
            <div
              style={{
                padding: "8px 10px",
                borderLeft: "0.5px solid var(--rule-soft)",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {row.current}
            </div>
            <div
              style={{
                padding: "8px 10px",
                borderLeft: "0.5px solid var(--rule-soft)",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                fontWeight: row.changed ? 600 : 400,
              }}
            >
              {row.imported}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewCard({
  entry,
  masterCache,
  onUpdate,
  onRemove,
}: {
  entry: PreviewEntry;
  masterCache: ReturnType<typeof createOrderMasterCache>;
  onUpdate: (updated: PreviewEntry) => void;
  onRemove: () => void;
}) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const draft = parsedPreviewToDraft(entry.preview);

  const hasPdf = !!entry.preview.uploaded_file_public_id;
  const confidencePct = Math.round((entry.preview.confidence ?? 1) * 100);
  const confidenceLevel =
    (entry.preview.confidence ?? 1) >= 0.8
      ? "high"
      : (entry.preview.confidence ?? 1) >= 0.5
        ? "mid"
        : "low";

  const orderNumber = draft.order_number.trim();

  const duplicateQuery = useQuery({
    queryKey: ["orders", "by-number", orderNumber],
    queryFn: async () => {
      const res = await api.order.list({ limit: 5, order_number: orderNumber });
      return res.data;
    },
    enabled: !!orderNumber && entry.status === "pending",
    staleTime: 30_000,
  });
  const existingDuplicate = duplicateQuery.data?.[0] ?? null;

  // order.list は明細 (items) を空で返すため、差分比較には order.get の詳細を使う。
  // list の結果のまま比較すると明細に毎回差分が出てしまう。
  const duplicateDetailQuery = useQuery({
    queryKey: ["orders", "detail", existingDuplicate?.id],
    queryFn: async () => {
      const res = await api.order.get({ publicId: existingDuplicate!.id });
      return res.data;
    },
    enabled: !!existingDuplicate?.id,
    staleTime: 30_000,
  });
  const existingForComparison = existingDuplicate
    ? (duplicateDetailQuery.data ?? existingDuplicate)
    : null;

  useEffect(() => {
    if (entry.status !== "pending") return;
    if (existingDuplicate?.id && entry.existingDuplicatePublicId !== existingDuplicate.id) {
      onUpdate({ ...entry, existingDuplicatePublicId: existingDuplicate.id });
      return;
    }
    if (duplicateQuery.isFetched && !existingDuplicate && entry.existingDuplicatePublicId) {
      onUpdate({ ...entry, existingDuplicatePublicId: null });
    }
  }, [
    existingDuplicate?.id,
    duplicateQuery.isFetched,
    entry.existingDuplicatePublicId,
    entry.status,
  ]);

  const uploadedFileId = entry.preview.uploaded_file_public_id;

  const confirmBlockers = getOrderBlockingReasons(draft);
  const cantImport = getDraftImportBlockers(draft).length > 0;

  const confirmMutation = useMutation({
    mutationFn: async (arg: {
      mode: "create" | "update";
      publicId?: string;
    }): Promise<{ orderId: string; createdDeliverySpot: boolean; createdPickupSpot: boolean }> => {
      const ensured = await ensureUnselectedOrderMasters({
        api,
        draft,
        coords: previewCoords(entry.preview),
        geocodeAddress: geocodeJpAddress,
        cache: masterCache,
      });
      if (arg.mode === "update" && arg.publicId) {
        const payload = buildImportUpdateOrderPayload(
          ensured.draft,
          ensured.coords,
          ensured.draft.uploaded_file_public_id,
        );
        const res = await api.order.update({ publicId: arg.publicId, ...payload });
        return {
          orderId: res.data.id,
          createdDeliverySpot: ensured.createdDeliverySpot,
          createdPickupSpot: ensured.createdPickupSpot,
        };
      }
      const payload = buildCreateOrderPayload(ensured.draft, ensured.coords);
      const res = await api.order.create(payload);
      return {
        orderId: res.data.id,
        createdDeliverySpot: ensured.createdDeliverySpot,
        createdPickupSpot: ensured.createdPickupSpot,
      };
    },
    onSuccess: async ({ orderId, createdDeliverySpot, createdPickupSpot }) => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (createdDeliverySpot) {
        await queryClient.invalidateQueries({ queryKey: ["delivery-spots"] });
      }
      if (createdPickupSpot) {
        await queryClient.invalidateQueries({ queryKey: ["pickup-spots"] });
      }
      onUpdate({
        ...entry,
        status: "confirmed",
        createdOrderPublicId: orderId,
        existingDuplicatePublicId: null,
        error: null,
      });
    },
    onError: (err) => {
      onUpdate({
        ...entry,
        error: err instanceof Error ? err.message : String(err),
      });
    },
  });

  return (
    <section
      style={{
        border: "0.5px solid var(--rule)",
        borderRadius: 4,
        background: "var(--bg-raised)",
      }}
    >
      <header
        style={{
          padding: "14px 18px",
          borderBottom: "0.5px solid var(--rule-soft)",
          background: "var(--bg-panel)",
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          gap: 14,
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 2,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 500 }}>{entry.sourceFileName}</div>
        </div>

        {confidenceLevel !== "high" && (
          <ConfidenceBadge level={confidenceLevel} pct={confidencePct} />
        )}

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {entry.status === "confirmed" ? (
            <>
              <span
                style={{
                  fontSize: 11,
                  padding: "4px 10px",
                  background: "var(--st-done-bg)",
                  color: "var(--st-done-ink)",
                  borderRadius: 2,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  lineHeight: 1.4,
                }}
              >
                ✓ 取込済み
              </span>
              {entry.createdOrderPublicId && (
                <Button
                  variant="outline"
                  size="sm"
                  render={
                    <a
                      href={`/orders/${entry.createdOrderPublicId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  }
                >
                  詳細を開く
                </Button>
              )}
            </>
          ) : (
            <>
              {existingDuplicate ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={cantImport || confirmMutation.isPending}
                    onClick={() =>
                      confirmMutation.mutate({ mode: "update", publicId: existingDuplicate.id })
                    }
                    style={{ background: "var(--accent)", color: "#fff" }}
                  >
                    {confirmMutation.isPending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    既存更新
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={cantImport || confirmMutation.isPending}
                    onClick={() => confirmMutation.mutate({ mode: "create" })}
                    style={{ background: "var(--bg-raised)", color: "var(--ink)" }}
                  >
                    {confirmMutation.isPending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Plus size={14} />
                    )}
                    新規作成
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  disabled={cantImport || confirmMutation.isPending}
                  onClick={() => confirmMutation.mutate({ mode: "create" })}
                >
                  {confirmMutation.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Plus size={14} />
                  )}
                  新規作成
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRemove}
                title="このプレビューを破棄"
                style={{ color: "var(--ink-3)" }}
              >
                破棄
              </Button>
            </>
          )}
        </div>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: hasPdf ? "minmax(0, 1fr) minmax(360px, 480px)" : "minmax(0, 1fr)",
          minHeight: 0,
        }}
        className="cp-import-preview-layout"
      >
        <div>
          {entry.error && (
            <div
              role="alert"
              style={{
                padding: "10px 18px",
                background: "oklch(0.96 0.05 30)",
                color: "var(--danger)",
                fontSize: 11,
                borderBottom: "0.5px solid var(--rule-soft)",
              }}
            >
              確定に失敗しました: {entry.error}
            </div>
          )}

          {confirmBlockers.length > 0 && entry.status === "pending" && (
            <ConfirmBlockers blockers={confirmBlockers} />
          )}

          <ImportComparisonPanel existing={existingForComparison} draft={draft} />
        </div>

        {hasPdf && (
          <div
            style={{
              borderLeft: "0.5px solid var(--rule-soft)",
              background: "var(--bg)",
              position: "sticky",
              top: 0,
              alignSelf: "start",
            }}
          >
            <OrderPdfPanel
              showDropZone={false}
              uploadedFilePublicId={uploadedFileId}
              sticky={false}
              stickyMaxHeight
            />
          </div>
        )}
      </div>
    </section>
  );
}
